import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import {
  initGA4,
  enableEnhancedConversions,
  trackWhatsAppClick,
  trackCallClick
} from '../utils/analytics';
import { GA4_ID } from '../utils/analyticsConfig';

const CLARITY_ID = process.env.REACT_APP_CLARITY_ID || '';

/**
 * Loads the Microsoft Clarity tracking script (session recordings + heatmaps).
 * Idempotent — bails out if Clarity is already initialised or no ID is set.
 * Uses the official inline snippet from clarity.microsoft.com, wrapped so
 * we avoid eval/parse on the initial render path. Loaded async — zero
 * blocking impact on FCP/LCP.
 */
function loadClarity() {
  if (!CLARITY_ID || typeof window === 'undefined' || window.clarity) return;
  (function (c, l, a, r, i, t, y) {
    c[a] = c[a] || function () { (c[a].q = c[a].q || []).push(arguments); };
    t = l.createElement(r);
    t.async = 1;
    t.src = 'https://www.clarity.ms/tag/' + i + '?ref=bwt';
    y = l.getElementsByTagName(r)[0];
    y.parentNode.insertBefore(t, y);
  })(window, document, 'clarity', 'script', CLARITY_ID);
}

/**
 * Headless component that mounts once at the App root.
 *
 * Responsibilities:
 * - Initialise GA4 (if REACT_APP_GA4_ID is set).
 * - Turn on Enhanced Conversions on the existing Google Ads tag.
 * - Globally intercept clicks on tel: and wa.me / WhatsApp links
 *   and fire the corresponding Google Ads conversion + GA4 event.
 *   This means every page (current + future) is tracked without
 *   per-page wiring.
 * - Send a GA4 page_view on every route change.
 */
const AnalyticsManager = () => {
  const location = useLocation();

  // One-time init — gtag.js is loaded asynchronously by index.html
  // on window 'load'. Poll briefly until it's ready, then init.
  useEffect(() => {
    loadClarity();
    let cancelled = false;
    const tryInit = () => {
      if (cancelled) return;
      if (typeof window.gtag === 'function') {
        enableEnhancedConversions();
        initGA4();
      } else {
        setTimeout(tryInit, 500);
      }
    };
    tryInit();
    return () => {
      cancelled = true;
    };
  }, []);

  // Track SPA route changes as GA4 page_views.
  useEffect(() => {
    if (typeof window.gtag !== 'function' || !GA4_ID) return;
    window.gtag('event', 'page_view', {
      page_path: location.pathname + location.search,
      page_location: window.location.href,
      send_to: GA4_ID
    });
  }, [location.pathname, location.search]);

  // Global click interception for tel: and WhatsApp links.
  // One delegated listener for the whole document — works for every
  // current and future page without touching individual components.
  useEffect(() => {
    const onClick = (e) => {
      const anchor = e.target.closest && e.target.closest('a[href]');
      if (!anchor) return;
      const href = anchor.getAttribute('href') || '';
      const source = anchor.getAttribute('data-testid') || anchor.textContent?.trim()?.slice(0, 40) || 'unknown';

      if (href.startsWith('tel:')) {
        trackCallClick(source);
        return;
      }
      if (href.includes('wa.me/') || href.includes('api.whatsapp.com')) {
        trackWhatsAppClick(source);
      }
    };
    document.addEventListener('click', onClick, true);
    return () => document.removeEventListener('click', onClick, true);
  }, []);

  return null;
};

export default AnalyticsManager;
